export const deduplicate = <T>(array: T[]): T[] => {
  return array.filter((item, index) => array.indexOf(item) === index);
};

export const deduplicateBy = <T>(
  array: T[],
  toStr: (item: T) => string
): T[] => {
  const values = array.map(toStr);
  return array.filter((_, index) => values.indexOf(values[index]) === index);
};

export const arrayRemove = <T>(arraySource: T[], arrayRemoved: T[]): T[] => {
  return arraySource.filter((item) => arrayRemoved.indexOf(item) === -1);
};

export const arrayIntersect = <T>(array1: T[], array2: T[]): T[] => {
  return array1.filter((item) => array2.indexOf(item) !== -1);
};

export const groupBy = <T>(
  array: T[],
  groupFct: (item: T) => string
): Record<string, T[]> => {
  const keys = array.map((item) => groupFct(item));
  return array.reduce<Record<string, T[]>>((groups, item, index) => {
    const key = keys[index];
    let group = groups[key];
    if (!group) {
      group = [];
      groups[key] = group;
    }
    group.push(item);
    return groups;
  }, {});
};

export const sortBy = <T>(
  array: T[],
  sortFct: (item: T) => string | number
): T[] => {
  return [...array].sort((a, b) => (sortFct(a) < sortFct(b) ? -1 : 1));
};

export const sortByMultiple = <T>(
  array: T[],
  sortFcts: ((item: T) => string | number)[]
): T[] => {
  return [...array].sort((a, b) => {
    const sortResult = sortFcts.reduce<number | undefined>((acc, sortFct) => {
      if (acc !== undefined) return acc;
      const valueA = sortFct(a);
      const valueB = sortFct(b);
      if (valueA < valueB) return -1;
      if (valueA > valueB) return 1;
      return acc;
    }, undefined);
    return sortResult === undefined ? 0 : sortResult;
  });
};

export const packBy = <T>(array: T[], packSize: number): T[][] => {
  const result = [] as T[][];
  let index = 0;
  while (index < array.length) {
    result.push(array.slice(index, index + packSize));
    index += packSize;
  }
  return result;
};

export const partition = <T>(
  array: T[],
  testFct: (item: T, index: number) => boolean
): { passing: T[]; notPassing: T[] } => {
  return array.reduce(
    (acc, item, index) => {
      if (testFct(item, index)) {
        acc.passing.push(item);
      } else {
        acc.notPassing.push(item);
      }
      return acc;
    },
    { passing: [] as T[], notPassing: [] as T[] }
  );
};

export const takeWhile = <T>(
  array: T[],
  testFct: (item: T, index: number) => boolean
): T[] => {
  const { taken } = array.reduce(
    (acc, item, index) => {
      if (acc.isDone) return acc;
      if (testFct(item, index)) {
        acc.taken.push(item);
      } else {
        acc.isDone = true;
      }
      return acc;
    },
    { taken: [] as T[], isDone: false }
  );
  return taken;
};

export const takeLeft = takeWhile;

export const takeRight = <T>(
  array: T[],
  testFct: (item: T, index: number) => boolean
): T[] => {
  return takeLeft([...array].reverse(), testFct).reverse();
};

export const findAndMap = <T, B>(
  array: T[],
  findFct: (item: T, index: number) => B | undefined
): { item: T; found: B } | undefined => {
  return array.reduce<{ item: T; found: B } | undefined>((acc, item, index) => {
    if (acc !== undefined) return acc;
    const found = findFct(item, index);
    if (found === undefined) return undefined;
    return { item, found };
  }, undefined);
};

export const arrayLast = <T>(array: T[]): T | undefined =>
  array.at(array.length - 1);

export const typedFilter = <A, B extends A>(
  array: A[],
  testFct: (item: A) => item is B
): B[] => {
  return array.filter(testFct);
};

export const maxBy = <T>(
  array: T[],
  valueFct: (item: T) => number
): { value: number; item: T } => {
  const firstItem = array.at(0);
  if (!firstItem) {
    throw new Error("expected a non-empty array");
  }
  return array.slice(1).reduce<{ value: number; item: T }>(
    (acc, item) => {
      const value = valueFct(item);
      if (value > acc.value) {
        acc.value = value;
        acc.item = item;
      }
      return acc;
    },
    { value: valueFct(firstItem), item: firstItem }
  );
};

export const seq = (length: number) => [...new Array(length)];
